import { designTokens } from '@cinch-labs/design-system'

const { color: palette } = designTokens.customProperties.ds

const colors = {
  basic: {
    white: palette.pampas,
    black: palette.acadia,
  },
  link: {
    active: palette.vividViolet,
    default: palette.deepPurple,
    focus: palette.vividViolet,
    hover: palette.vividViolet,
    visited: palette.deepPurple,
  },
  brand: {
    main: palette.deepPurple,
    secondary: palette.pearlescentPink,
  },
  ui: {
    success: palette.vidaLoca,
    error: palette.monza,
    focus: palette.pearlescentPink,
    divider: palette.gainsboro,
    selected: palette.vividViolet,
  },
  text: {
    ui: palette.deepPurple,
    main: palette.deepPurple,
    display: palette.mineShaft,
    body: palette.mineShaft,
    meta: palette.asphaltGrey,
    paneltitle: palette.deepPurple,
    primaryblack: palette.mineShaft,
  },
  gradient: {
    alpha: {
      a: palette.deepPurple,
      b: palette.pearlescentPink,
    },
    bravo: {
      a: palette.iceBlue,
      b: palette.goGreen,
    },
    charlie: {
      a: palette.vividViolet,
      b: palette.iceBlue,
    },
    delta: {
      a: palette.vividViolet,
      b: palette.warmOrange,
    },
  },
  shade: {
    xxlight: palette.wildSand,
    xlight: palette.gainsboro,
    light: palette.gainsboro,
    medium: palette.asphaltGrey,
    dark: palette.mineShaft,
  },
  panel: {
    a: palette.wildSand,
  },
}

const opacities = {
  low: 0.75,
  medium: 0.5,
  high: 0.25,
}

const fontWeights = {
  light: 200,
  book: 300,
  medium: 400,
  semibold: 600,
  bold: 700,
}

const fontSizes = {
  '5xl': '72px',
  '4xl': '42px',
  '3xl': '32px',
  xxl: '28px',
  xl: '24px',
  l: '22px',
  m: '18px',
  s: '16px',
  xs: '14px',
  '3xs': '12px',
  '4xs': '10px',
}

const spacing = {
  none: '0',
  xs: '4px',
  s: '8px',
  m: '16px',
  l: '20px',
  xl: '32px',
  xxl: '64px',
}

const dimensions = {
  slim: '1024px',
  max: '1280px',
}

const breakpoints = {
  xxxs: { min: 375, max: 0 },
  xxs: { min: 0, max: 599 },
  xs: { min: 600, max: 767 },
  s: { min: 768, max: 1023 },
  m: { min: 1024, max: 1439 },
  l: { min: 1440, max: 1919 },
  xl: { min: 1920, max: Infinity },
}

const mediaQueries = {
  xxxs: `(min-width: ${breakpoints.xxxs.min}px)`,
  xxs: `(max-width: ${breakpoints.xxs.max}px)`,
  xs: `(min-width: ${breakpoints.xs.min}px)`,
  s: `(min-width: ${breakpoints.s.min}px)`,
  m: `(min-width: ${breakpoints.m.min}px)`,
  l: `(min-width: ${breakpoints.l.min}px)`,
  xl: `(min-width: ${breakpoints.xl.min}px)`,
  mediumPhone: `(min-height: ${breakpoints.xxs.max}px)`,
  mediumDesktop: `(min-height: ${breakpoints.s.min}px) and (min-width: ${breakpoints.m.min}px)`,
}

const toreaBayAlpha = '#1565C04D' // Torea Bay with a 30% opacity

const components = {
  'delivery-details': {
    bg: palette.water,
  },
  'delivery-slot-picker': {
    'max-width': '540px',
    'cell-button-bg': palette.pampas,
    'cell-button-color': palette.mineShaft,
    'cell-button-border-color': palette.gainsboro,
    'cell-button-hover-bg': palette.wildSand,
    'cell-button-selected-bg': palette.wildSand,
    'cell-button-selected-border': palette.vidaLoca,
    'cell-button-disabled-bg': palette.gainsboro,
    'cell-button-disabled-color': palette.asphaltGrey,
    'cell-button-disabled-hover-color': palette.pampas,
    'header-cell-text-color': palette.pampas,
    'header-cell-gradient-start': colors.gradient.charlie.a,
    'header-cell-gradient-stop': colors.gradient.charlie.b,
    'pagination-button-color': palette.toreaBay,
    'pagination-header-color': palette.mineShaft,
    'pagination-button-hover-bg': palette.gainsboro,
  },
  'expandable-panel': {
    'banner-height': '60px',
    'transition-speed': '0.6s',
  },
  'order-details': {
    bg: palette.water,
    'deduction-text': palette.vividViolet,
  },
  'order-details-panel': {
    'banner-label-color': palette.toreaBay,
    'bg-closed': palette.water,
    'border-color': toreaBayAlpha,
    'border-radius': '15px',
    'button-icon-color': palette.deepPurple,
    'button-focus-border-color': palette.iceBlue,
    'row-border-color': palette.gainsboro,
    'text-color-light': '#707070',
    'text-color-dark': palette.mineShaft,
    'text-color-negative': palette.vividViolet,
    'to-pay-background-color': palette.water,
    'to-pay-border-color': palette.asphaltGrey,
  },
  forms: {
    'checkbox-svg': '1.5em',
  },
  stripe: {
    'field-border': palette.asphaltGrey,
    'field-hover-border': palette.mineShaft,
    'field-text': palette.deepPurple,
    'field-placeholder-text': palette.asphaltGrey,
    'field-focus-border': palette.deepPurple,
    'field-disabled': palette.wildSand,
    'field-invalid-border': palette.monza,
  },
  'order-confirmation': {
    'order-number-text': palette.goGreen,
    'card-bg': palette.wildSand,
    'title-color': palette.deepPurple,
  },
  checkout: {
    nav: {
      'label-text': palette.meteorite,
      'button-bg': palette.water,
      'button-text': palette.toreaBay,
      'button-border': toreaBayAlpha,
    },
    'step-drawer': {
      bg: palette.pampas,
      'overlay-bg': palette.acadia,
      'banner-fg': palette.pampas,
      'header-fg': palette.mineShaft,
      'header-border': palette.asphaltGrey,
      'header-close-btn-fg': palette.meteorite,
      'header-close-btn-bg': palette.water,
      'header-close-btn-border': toreaBayAlpha,
      'status-icon-incomplete-border': toreaBayAlpha,
      'status-icon-incomplete-fg': palette.toreaBay,
      'status-icon-incomplete-bg': palette.water,
      'status-icon-complete-fg': palette.pampas,
      'status-icon-complete-bg': palette.meteorite,
      'status-icon-trail': toreaBayAlpha,
      'step-current-bg': palette.water,
      'step-disabled-fg': palette.asphaltGrey,
      'step-time-estimate-fg': palette.dove,
      'step-arrow-fg': palette.meteorite,
    },

    delivery: {
      'section-spacer-bg': palette.wildSand,
      'subtitle-color': palette.dove,
    },

    'personal-details': {
      'header-bg': palette.water,
      'header-icon': palette.meteorite,
      'header-title-fg': palette.meteorite,
      'header-text-fg': palette.dove,
      'preamble-header-fg': palette.mineShaft,
      'preamble-text-fg': palette.dove,
      'section-spacer-bg': palette.wildSand,
      'form-error-fg': palette.monza,
    },
  },
  youtube: {
    'button-bg': palette.pampas,
  },
  'checkout-warning-card': {
    'border-color': palette.brightSun,
    'bg-color': palette.earlyDawn,
    'title-color': palette.mineShaft,
    'body-text-color': palette.dove,
    'footer-cta-color': palette.deepPurple,
  },
  'checkout-form-card': {
    'border-color': palette.asphaltGrey,
    'bg-color': palette.wildSand,
    'title-color': palette.deepPurple,
    'text-color': palette.dove,
  },
  'brief-representative': {
    color: palette.deepPurple,
    bg: palette.water,
    link: palette.deepPurple,
  },
  quote: {
    'type-color': palette.deepPurple,
    'subtype-color': palette.dove,
    'text-color': palette.mineShaft,
    'explained-color': palette.deepPurple,
  },
  finance: {
    'employment-section': {
      bg: palette.wildSand,
      'border-color': palette.asphaltGrey,
      'title-color': palette.deepPurple,
    },
  },
}

export default {
  customProperties: {
    ds: designTokens.customProperties.ds,
    breakpoint: breakpoints,
    color: colors,
    opacities,
    spacing,
    dimensions,
    font: {
      size: fontSizes,
      weight: fontWeights,
    },
    components,
  },
  customMedia: {
    ds: designTokens.customMedia,
    ...mediaQueries,
  },
  environmentVariables: {},
  customSelectors: {},
}
